import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  LayoutDashboard, MonitorPlay, Radio, Sparkles, Trophy, Beer, Users,
  CalendarClock, Activity, ArrowRight, AlertTriangle, Wifi, WifiOff, Circle,
  Server, Database, Gauge, UserCheck, UserPlus, LifeBuoy,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import AccentCard from '@/components/AccentCard'
import PageHeader from '@/components/PageHeader'
import {
  screensAPI, programmeAPI, kassaAPI, visionAPI, imagecompoAPI,
  tournamentsAPI, playersAPI, adminAPI, gameServersAPI, settingsAPI, pterodactylAPI,
} from '@/lib/api'
import { useScreensSocket } from '@/lib/screensSocket'
import { useKassaSocket } from '@/lib/kassaSocket'
import { useProgrammeSocket } from '@/lib/programmeSocket'

/* Regie-dashboard: wat je tijdens de avond in één blik wil weten.

   De volgorde is die van een avond die misgaat: eerst of de schermen nog
   doen wat ze moeten, dan of het programma nog klopt, dan de shows, dan
   de bar. Wat aandacht vraagt springt eruit; de rest blijft rustig. */

const KV_FASEN = {
  setup: 'Setup', live_songs: 'Nummers', jury_reveal: 'Jury',
  public_vote: 'Publiek stemt', public_reveal: 'Publiek reveal', finished: 'Klaar',
}
const KV_ORDER = ['setup','live_songs','jury_reveal','public_vote','public_reveal','finished']
const IC_FASEN = {
  draft: 'Draft', submissions: 'Inschrijvingen', live: 'Bezig',
  pauze: 'Pauze', reveal: 'Reveal', done: 'Klaar',
}
const IC_ORDER = ['draft','submissions','live','pauze','reveal','done']

export default function RegieDashboardPage() {
  const [schermen, setSchermen] = useState([])
  const [prog, setProg]         = useState({ now: [], next: [] })
  const [kassa, setKassa]       = useState(null)
  const [kv, setKv]             = useState(null)
  const [ic, setIc]             = useState(null)
  const [toernooien, setToern]  = useState([])
  const [spelers, setSpelers]   = useState(0)
  const [health, setHealth]     = useState(null)
  const [servers, setServers]   = useState([])
  const [hub, setHub]           = useState(null)
  const [siteStatus, setSiteStatus] = useState(null) // { publicMode, funnelOn }
  const [participants, setParticipants] = useState(null) // { online_last_hour, team_requests_open, helpdesk_open }

  const laadSchermen = useCallback(() => {
    screensAPI.list().then(r => setSchermen(r.data?.screens ?? [])).catch(() => {})
  }, [])
  const laadProg = useCallback(() => {
    programmeAPI.getNow().then(r => setProg({ now: r.data?.now ?? [], next: r.data?.next ?? [] })).catch(() => {})
  }, [])
  const laadKassa = useCallback(() => {
    Promise.all([
      kassaAPI.getActiveEvent().catch(() => ({ data: {} })),
      kassaAPI.getLeaderboard().catch(() => ({ data: {} })),
    ]).then(([e, l]) => setKassa({
      event: e.data?.event ?? null,
      topN:  (l.data?.leaderboard ?? []).slice(0, 3),
    })).catch(() => {})
  }, [])
  const laadSysteem = useCallback(() => {
    adminAPI.getSystemHealth()
      .then(r => setHealth(r.data ?? null))
      .catch(() => setHealth(null))
    // Merge handmatige game_servers met Pterodactyl-servers. Regie liet
    // vroeger alleen de eerste categorie zien; Pterodactyl-servers hebben
    // hun eigen bron. Beide normaliseren naar dezelfde shape.
    Promise.allSettled([
      gameServersAPI.getServers(),
      pterodactylAPI.getServersBasic(),
    ]).then(([manRes, ptRes]) => {
      const manual = (manRes.value?.data?.servers ?? manRes.value?.data ?? []).map(s => ({
        id: `m-${s.id}`,
        name: s.name,
        status: s.status || 'unknown',
        ip_address: s.ip_address,
        port: s.port,
        current_players: s.current_players,
        max_players: s.max_players,
        source: 'manual',
      }))
      const pt = (ptRes.value?.data?.servers ?? []).map(s => ({
        id: `p-${s.identifier}`,
        name: s.name,
        status: s.suspended ? 'maintenance' : 'online',
        ip_address: s.ip,
        port: s.port,
        source: 'pterodactyl',
      }))
      setServers([...manual, ...pt])
    })
  }, [])

  const laadRest = useCallback(() => {
    visionAPI.getActiveShow().then(r => setKv(r.data?.show ?? null)).catch(() => {})
    imagecompoAPI.getActiveShow().then(r => setIc(r.data?.show ?? null)).catch(() => {})
    tournamentsAPI.getTournaments()
      .then(r => setToern(r.data?.tournaments ?? r.data ?? []))
      .catch(() => {})
    playersAPI.list().then(r => setSpelers((r.data?.players ?? []).length)).catch(() => {})
    // AdminHub — pending-todos in Regie inpluggen zodat er niet 2 dashboards
    // om aandacht vechten. AdminHub-pagina zelf blijft bereikbaar via redirect.
    adminAPI.getHub().then(r => setHub(r.data)).catch(() => setHub(null))
    adminAPI.getRegieParticipants().then(r => setParticipants(r.data)).catch(() => setParticipants(null))
    // Site-mode indicator — één blik op publieke modus + funnel.
    Promise.allSettled([
      settingsAPI.getPublicMode?.() || Promise.resolve({}),
      adminAPI.getFunnelStatus(),
    ]).then(([pm, fn]) => setSiteStatus({
      publicMode: !!pm.value?.data?.public_mode,
      funnelOn:   !!fn.value?.data?.aan,
    })).catch(() => setSiteStatus(null))
  }, [])

  useEffect(() => { laadSchermen(); laadProg(); laadKassa(); laadRest(); laadSysteem() },
            [laadSchermen, laadProg, laadKassa, laadRest, laadSysteem])

  useScreensSocket(laadSchermen)
  useProgrammeSocket(laadProg)
  useKassaSocket(laadKassa)

  // Schermstatus verloopt na 45s; en shows wisselen zonder dat wij een socket hebben.
  useEffect(() => {
    const t = setInterval(() => { laadSchermen(); laadRest(); laadSysteem() }, 25_000)
    return () => clearInterval(t)
  }, [laadSchermen, laadRest, laadSysteem])

  const online  = schermen.filter(s => s.is_online)
  const offline = schermen.filter(s => !s.is_online)
  const zwart   = online.filter(s => !s.current_route)
  const live    = toernooien.filter(t => ['active', 'in_progress', 'ongoing'].includes(t.status))

  // Alles wat je nú zou willen weten omdat het niet klopt.
  const waarschuwingen = [
    offline.length > 0 && {
      tekst: `${offline.length} ${offline.length === 1 ? 'scherm is' : 'schermen zijn'} offline`,
      detail: offline.map(s => s.name).join(', '),
      naar: '/schermregie',
    },
    zwart.length > 0 && {
      tekst: `${zwart.length} ${zwart.length === 1 ? 'scherm toont' : 'schermen tonen'} niets`,
      detail: zwart.map(s => s.name).join(', '),
      naar: '/schermregie',
    },
    schermen.length === 0 && {
      tekst: 'Nog geen schermen geregistreerd',
      detail: 'Voeg je beamers toe zodat je ze centraal kunt bedienen',
      naar: '/schermregie',
    },
    // Een dienst die niet antwoordt merk je anders pas als iemand klaagt.
    onbereikbaar(health).length > 0 && {
      tekst: `${onbereikbaar(health).length} dienst(en) reageren niet`,
      detail: onbereikbaar(health).map(d => d.name).join(', '),
      naar: '/admin/system-health',
    },
    // Alleen handmatige game_servers voor offline-warning. Pterodactyl-status
    // vergt de volle endpoint (basic geeft 'unknown'). Voor die servers checken
    // we hoogstens 'maintenance' (suspended door admin).
    (() => {
      const off = servers.filter(sv => sv.source === 'manual' && sv.status !== 'online')
      return off.length > 0 && {
        tekst: `${off.length} gameserver(s) offline`,
        detail: off.map(sv => sv.name).join(', '),
        naar: '/game-servers',
      }
    })(),
    // AdminHub-signalen als eerste-orde regie-waarschuwingen
    (hub?.counts?.tournaments_pending_award ?? 0) > 0 && {
      tekst: `${hub.counts.tournaments_pending_award} toernooi(en) wachten op uitreiking`,
      detail: (hub.todos?.tournaments_to_award || []).slice(0, 3).map(t => t.name).join(' · '),
      naar: '/tournaments',
    },
    (hub?.counts?.unhanded_prize_claims ?? 0) > 0 && {
      tekst: `${hub.counts.unhanded_prize_claims} prijs(claim) wacht op afhandeling`,
      detail: (hub.todos?.unclaimed_prizes || []).slice(0, 3).map(p => p.prize_name).join(' · '),
      naar: '/prijzenkraam/admin',
    },
    /* NB: team-verzoeken zijn per definitie per user (invite) of per kapitein
       (join-request). Geen admin-taak — dus GEEN Regie-warning. Ze wachten
       op de user zelf via /mijn-verzoeken. */
    ...(hub?.live?.stale_matches || []).map(m => ({
      tekst: `Match wacht al ${m.minuten}m op resultaat`,
      detail: `${m.tournament_name} — ronde ${m.round} match ${m.match_number}`,
      naar: `/tournaments/${m.tournament_id}`,
    })),
  ].filter(Boolean)

  return (
    <div>
      <PageHeader
        icon={LayoutDashboard}
        title="Regie"
        subtitle="Alles wat vanavond telt, op één scherm"
        actions={siteStatus && (
          <div className="flex items-center gap-2">
            {(siteStatus.publicMode || siteStatus.funnelOn) ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-peach/40 bg-peach/10 text-peach font-mono text-[10px] uppercase tracking-widest"
                    title="Publieke modus of Funnel actief — server is bereikbaar via internet">
                <span className="live-dot" style={{ background: 'currentColor' }} />
                Publiek {siteStatus.funnelOn ? '(Funnel)' : ''}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-live/40 bg-live/10 text-live font-mono text-[10px] uppercase tracking-widest"
                    title="Alleen bereikbaar op LAN en tailnet">
                LAN-only
              </span>
            )}
          </div>
        )}
      />

      {waarschuwingen.length > 0 && (
        <div className="space-y-2 mb-4">
          {waarschuwingen.map((w, i) => (
            <Link key={i} to={w.naar} className="block no-underline">
              <AccentCard tone="peach" className="hover:border-peach">
                <CardContent className="p-3 flex items-center gap-3">
                  <AlertTriangle className="h-4 w-4 text-peach shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{w.tekst}</div>
                    <div className="text-xs text-muted-foreground truncate">{w.detail}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </CardContent>
              </AccentCard>
            </Link>
          ))}
        </div>
      )}

      {/* Kerncijfers */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5 mb-4">
        <Cijfer icon={MonitorPlay} kleur="#61E294"
                waarde={`${online.length}/${schermen.length}`} label="schermen online" naar="/schermregie" />
        <Cijfer icon={Trophy} kleur="#E4C265"
                waarde={live.length} label={live.length === 1 ? 'toernooi bezig' : 'toernooien bezig'} naar="/tournaments" />
        <Cijfer icon={Users} kleur="#7BC8FF"
                waarde={hub?.counts?.active_users ?? spelers} label={hub?.counts?.active_users != null ? 'actief (30min)' : 'deelnemers'} naar="/spelers" />
        <Cijfer icon={Users} kleur="#94A3B8"
                waarde={spelers} label="totaal deelnemers" naar="/spelers" />
        <Cijfer icon={Beer} kleur="#FFB57A"
                waarde={kassa?.event ? 'open' : 'dicht'} label="bar" naar="/bestellen" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">

        {/* Schermen — het hart van de regie */}
        <Blok icon={MonitorPlay} titel="Schermen" naar="/schermregie" actie="Regie openen">
          {schermen.length === 0 ? (
            <Leeg tekst="Nog geen schermen geregistreerd." />
          ) : (
            <div className="space-y-1.5">
              {schermen.map(s => (
                <div key={s.id} className="flex items-center gap-2.5 text-sm">
                  {s.is_online
                    ? <Wifi className="h-3.5 w-3.5 text-live shrink-0" />
                    : <WifiOff className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                  <span className="font-semibold truncate w-32 shrink-0">{s.name}</span>
                  <span className={`truncate flex-1 text-xs ${s.current_route ? 'text-muted-foreground' : 'text-peach'}`}>
                    {s.current_route ?? 'niets'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Blok>

        {/* Programma */}
        <Blok icon={CalendarClock} titel="Programma" naar="/programma/admin" actie="Agenda">
          {prog.now.length === 0 && prog.next.length === 0 ? (
            <Leeg tekst="Niets ingepland." />
          ) : (
            <div className="space-y-2">
              {prog.now.map(s => (
                <div key={s.id} className="flex items-start gap-2.5">
                  <Circle className="h-2 w-2 fill-live text-live shrink-0 mt-1.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{s.title}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-live">
                      bezig · {s.track}
                    </div>
                  </div>
                </div>
              ))}
              {prog.next.slice(0, 3).map(s => (
                <div key={s.id} className="flex items-baseline gap-2.5 text-sm">
                  <span className="font-mono tabular-nums text-muted-foreground w-11 shrink-0">
                    {new Date(s.starts_at).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="truncate flex-1 text-muted-foreground">{s.title}</span>
                </div>
              ))}
            </div>
          )}
        </Blok>

        {/* Shows */}
        <Blok icon={Radio} titel="Compo's &amp; uitreiking">
          <div className="space-y-2.5">
            <ShowRegel
              icon={Radio} kleur="#B4FF39" naam="Kempenvision"
              show={kv} fasen={KV_FASEN} order={KV_ORDER}
              naar="/kempenvision/control"
              onNextPhase={async (phase) => {
                await visionAPI.updateState({ phase })
                laadRest()
              }}
            />
            <ShowRegel
              icon={Sparkles} kleur="#C77BFF" naam="Image Compo"
              show={ic} fasen={IC_FASEN} order={IC_ORDER}
              naar="/imagecompo/control"
              onNextPhase={async (phase) => {
                await imagecompoAPI.updateState({ phase })
                laadRest()
              }}
            />
            {(hub?.counts?.tournaments_pending_award ?? 0) > 0 && (
              <RegelLink
                label={<><Trophy className="h-3.5 w-3.5 inline mr-1 opacity-70" />Toernooien wachten op uitreiking</>}
                waarde={String(hub.counts.tournaments_pending_award)}
                naar={hub.todos?.tournaments_to_award?.[0]?.id
                  ? `/tournaments/${hub.todos.tournaments_to_award[0].id}?tab=uitreiking`
                  : '/tournaments'}
                highlight
              />
            )}
            {(participants?.compos_pending_award?.count ?? 0) > 0 && (
              <RegelLink
                label={<><Trophy className="h-3.5 w-3.5 inline mr-1 opacity-70" />Compo-badges wachten op toewijzing</>}
                waarde={String(participants.compos_pending_award.count)}
                naar={
                  participants.compos_pending_award.first?.type === 'imagecompo'
                    ? '/imagecompo/admin?tab=uitreiking'
                    : '/kempenvision/admin?tab=uitreiking'
                }
                highlight
              />
            )}
            {(hub?.counts?.unhanded_prize_claims ?? 0) > 0 && (
              <RegelLink
                label={<><Sparkles className="h-3.5 w-3.5 inline mr-1 opacity-70" />Prijzen wachten op afhandeling</>}
                waarde={String(hub.counts.unhanded_prize_claims)}
                naar="/prijzenkraam/admin"
                highlight
              />
            )}
          </div>
        </Blok>

        {/* Bar &amp; kassa — event + omzet + top-3 + raffle gegund vandaag */}
        <Blok icon={Beer} titel="Bar &amp; kassa" naar="/bestellen" actie="Bestellen">
          {!kassa?.event ? (
            <Leeg tekst="Geen bar-event actief." />
          ) : (
            <div className="space-y-1.5 text-sm">
              <Regel label="Event" waarde={kassa.event.name || 'open'} />
              {participants?.bar && (
                <>
                  <Regel label="Besteld vandaag" waarde={`€${(participants.bar.total_cents / 100).toFixed(2).replace('.', ',')}`} />
                  <Regel label="Open rekeningen" waarde={String(participants.bar.open_accounts)} />
                </>
              )}
              {participants && participants.raffle_granted_today > 0 && (
                <Regel label="Raffle-tickets vandaag" waarde={String(participants.raffle_granted_today)} />
              )}
              {kassa.topN?.length > 0 && (
                <div className="pt-1.5 border-t border-border/40">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Top-drinkers</div>
                  <ol className="space-y-0.5">
                    {kassa.topN.map((t, i) => (
                      <li key={i} className="flex items-baseline justify-between gap-2">
                        <span className="truncate">
                          <span className="text-muted-foreground mr-1.5 tabular-nums">{i + 1}.</span>
                          {t.customer?.name ?? t.name ?? '—'}
                        </span>
                        <span className="font-mono text-xs text-muted-foreground tabular-nums">
                          {t.total_items ?? t.count ?? t.orders ?? 0}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
        </Blok>

        {/* Deelnemers & inschrijvingen — snelle regie-gevoel. */}
        <Blok icon={Users} titel="Deelnemers">
          <div className="space-y-1.5 text-sm">
            <Regel
              label={<><UserCheck className="h-3.5 w-3.5 inline mr-1 opacity-70" />Online laatste uur</>}
              waarde={participants ? String(participants.online_last_hour) : '—'}
            />
            {/* Team-verzoeken zijn per definitie een zaak tussen user en
                kapitein (per memory: geen admin-inbox). We tonen alleen nog
                het cijfer als context, zonder link — anders klik je naar
                /teams waar toch geen afhandelbare inbox staat. */}
            <Regel
              label={<><UserPlus className="h-3.5 w-3.5 inline mr-1 opacity-70" />Team-verzoeken open</>}
              waarde={participants ? String(participants.team_requests_open) : '—'}
            />
            <RegelLink
              label={<><LifeBuoy className="h-3.5 w-3.5 inline mr-1 opacity-70" />Onbeantwoorde tickets</>}
              waarde={participants ? String(participants.helpdesk_open) : '—'}
              naar="/helpdesk-panel"
              highlight={participants?.helpdesk_open > 0}
            />
          </div>
        </Blok>

      </div>

      {/* Systeem en omgeving */}
      <div className="grid gap-4 lg:grid-cols-2 mt-4">

        <Blok icon={Activity} titel="Diensten" naar="/admin/system-health" actie="Details">
          {!health?.services?.length ? (
            <Leeg tekst="Statusgegevens niet beschikbaar." />
          ) : (
            <div className="space-y-1.5">
              {health.services.map((d, i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm">
                  <span className="h-2 w-2 rounded-full shrink-0"
                        style={{ background: d.reachable ? '#61E294' : '#FF5A5A' }} />
                  <span className="font-semibold truncate flex-1">{d.name}</span>
                  {d.reachable ? (
                    <span className="font-mono text-xs tabular-nums text-muted-foreground shrink-0">
                      {d.latency_ms} ms
                    </span>
                  ) : (
                    <span className="font-mono text-xs text-signal shrink-0">geen antwoord</span>
                  )}
                </div>
              ))}
              {health.postgres && (
                <div className="flex items-center gap-2.5 text-sm pt-1.5 mt-1.5 border-t border-border/40">
                  <Database className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground flex-1">Database</span>
                  <span className="font-mono text-xs tabular-nums shrink-0">
                    {health.postgres.db_size} · {health.postgres.active_connections} verbinding(en)
                  </span>
                </div>
              )}
              {health.postgres?.uptime_seconds && (
                <div className="flex items-center gap-2.5 text-sm">
                  <Gauge className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground flex-1">Draait al</span>
                  <span className="font-mono text-xs shrink-0">
                    {uptimeTekst(health.postgres.uptime_seconds)}
                  </span>
                </div>
              )}
            </div>
          )}
        </Blok>

        <Blok icon={Server} titel="Game servers" naar="/game-servers" actie="Beheren">
          {servers.length === 0 ? (
            <Leeg tekst="Geen servers geconfigureerd." />
          ) : (
            <div className="space-y-1.5">
              {servers.slice(0, 6).map(sv => (
                <div key={sv.id} className="flex items-center gap-2.5 text-sm">
                  <span className="h-2 w-2 rounded-full shrink-0"
                        style={{ background: sv.status === 'online' ? '#61E294' : '#94A3B8' }} />
                  <span className="font-semibold truncate w-28 shrink-0">{sv.name}</span>
                  <span className="font-mono text-xs text-muted-foreground truncate flex-1">
                    {sv.ip_address}{sv.port ? `:${sv.port}` : ''}
                  </span>
                  {sv.status === 'online' && (
                    <span className="font-mono text-xs tabular-nums text-live shrink-0">
                      {sv.current_players ?? 0}/{sv.max_players ?? '?'}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Blok>

      </div>

      {/* Snelkoppelingen — direct-naar én diagnostics in één band. */}
      <Card className="mt-4">
        <CardContent className="p-4 space-y-3">
          <div>
            <div className="eyebrow mb-2">Direct naar</div>
            <div className="flex gap-2 flex-wrap">
              {[
                { naar: '/schermregie',            label: 'Schermregie',      icon: MonitorPlay },
                { naar: '/kempenvision/control',   label: 'KV control',       icon: Radio },
                { naar: '/imagecompo/control',     label: 'IC control',       icon: Sparkles },
                { naar: '/programma/admin',        label: 'Agenda',           icon: CalendarClock },
                { naar: '/tournaments',            label: 'Toernooien',       icon: Trophy },
                { naar: '/admin/setup',            label: 'Setup-hub',        icon: LayoutDashboard },
              ].map(k => {
                const Icon = k.icon
                return (
                  <Button key={k.naar} asChild size="sm" variant="outline">
                    <Link to={k.naar}><Icon className="h-4 w-4 mr-1.5" />{k.label}</Link>
                  </Button>
                )
              })}
            </div>
          </div>
          <div className="pt-3 border-t border-border/40">
            <div className="eyebrow mb-2">Diagnostics — als er iets misgaat</div>
            <div className="flex gap-2 flex-wrap">
              {[
                { naar: '/admin/system-health', label: 'System health',  icon: Activity },
                { naar: '/admin/analytics',     label: 'Analytics',      icon: Gauge },
                { naar: '/network-tools',       label: 'Network tools',  icon: Wifi },
                { naar: '/game-console',        label: 'Game console',   icon: Server },
              ].map(k => {
                const Icon = k.icon
                return (
                  <Button key={k.naar} asChild size="sm" variant="outline">
                    <Link to={k.naar}><Icon className="h-4 w-4 mr-1.5" />{k.label}</Link>
                  </Button>
                )
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

/* ── Bouwstenen ──────────────────────────────────────────────────── */

function Cijfer({ icon: Icon, kleur, waarde, label, naar }) {
  const inner = (
    <Card className="transition-colors hover:border-acid h-full" style={{ borderLeft: `2px solid ${kleur}` }}>
      <CardContent className="p-4">
        <Icon className="h-4 w-4 mb-2" style={{ color: kleur }} />
        <div className="text-2xl font-bold tabular-nums leading-none">{waarde}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
      </CardContent>
    </Card>
  )
  return naar ? <Link to={naar} className="block no-underline">{inner}</Link> : inner
}

function Blok({ icon: Icon, titel, naar, actie, children }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{titel}</span>
          {naar && (
            <Button asChild size="sm" variant="ghost" className="ml-auto h-6 text-xs">
              <Link to={naar}>{actie}<ArrowRight className="h-3 w-3 ml-1" /></Link>
            </Button>
          )}
        </div>
        {children}
      </CardContent>
    </Card>
  )
}

function ShowRegel({ icon: Icon, kleur, naam, show, fasen, order, naar, onNextPhase }) {
  if (!show) {
    return (
      <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
        <Icon className="h-3.5 w-3.5 shrink-0 opacity-40" />
        <span className="w-32 shrink-0">{naam}</span>
        <span className="text-xs">geen actieve show</span>
      </div>
    )
  }
  const idx = order?.indexOf(show.phase) ?? -1
  const nextPhase = idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null
  const [busy, setBusy] = useState(false)
  const advance = async (e) => {
    e.preventDefault(); e.stopPropagation()
    if (!nextPhase || busy) return
    setBusy(true)
    try { await onNextPhase(nextPhase) } finally { setBusy(false) }
  }
  return (
    <div className="flex items-center gap-2.5 text-sm">
      <Link to={naar} className="flex items-center gap-2.5 no-underline hover:opacity-80 flex-1 min-w-0">
        <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: kleur }} />
        <span className="w-32 shrink-0 font-semibold truncate">{show.name}</span>
        <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: kleur }}>
          {fasen[show.phase] ?? show.phase}
        </span>
      </Link>
      {nextPhase && (
        <Button size="sm" variant="outline" onClick={advance} disabled={busy} className="h-6 px-2 text-[10px]">
          → {fasen[nextPhase] ?? nextPhase}
        </Button>
      )}
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
    </div>
  )
}

function Regel({ label, waarde }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium truncate">{waarde}</span>
    </div>
  )
}

/** Regel met klik-doel. Highlight = acid-kleur op de waarde (0 blijft grijs). */
function RegelLink({ label, waarde, naar, highlight }) {
  const content = (
    <div className="flex items-baseline justify-between gap-3 group">
      <span className="text-muted-foreground group-hover:text-foreground transition-colors">{label}</span>
      <span className={`font-medium truncate ${highlight ? 'text-acid' : ''}`}>{waarde}</span>
    </div>
  )
  return naar ? <Link to={naar} className="no-underline block">{content}</Link> : content
}

function Leeg({ tekst }) {
  return <p className="text-sm text-muted-foreground">{tekst}</p>
}

/** Diensten die niet antwoorden — die wil je meteen zien. */
function onbereikbaar(health) {
  return (health?.services ?? []).filter(d => !d.reachable)
}

/** "3 dagen, 4 uur" leest prettiger dan een hoop seconden. */
function uptimeTekst(sec) {
  if (!sec) return '—'
  const d = Math.floor(sec / 86400)
  const u = Math.floor((sec % 86400) / 3600)
  const m = Math.floor((sec % 3600) / 60)
  if (d) return `${d} ${d === 1 ? 'dag' : 'dagen'}, ${u} uur`
  if (u) return `${u} uur, ${m} min`
  return `${m} min`
}
